<template>
  <div class="sidebar-container relative">
    <!-- 主侧边栏 -->
    <t-aside
      ref="sidebarRef"
      class="h-full bg-[#020202] flex flex-none flex-col pt-[24px] px-[15px] will-change-auto justify-between items-start overflow-hidden close-animation"
      :style="{ width: collapsed ? '70px' : '250px' }"
    >
      <!-- 一级菜单 -->
      <div class="sidebar-header w-full flex flex-col items-start">
        <!-- 头部logo -->
        <div>
          <logo :short="collapsed" />
        </div>
        <!-- 分割线 -->
        <div class="w-full my-4">
          <div class="border-t border-slate-500 w-full" />
        </div>
        <!-- 一级菜单内容 -->
        <div class="space-y-[24px]">
          <!-- 查询游戏搜索栏 -->
          <select-game-code :short="collapsed" />

          <!-- 一级菜单路由 -->
          <router-link
            v-for="router in topRoute"
            :key="router.path"
            :to="router.path"
            class="block"
          >
            <NavItem
              ref="navItemRefs"
              :icon-display-level="0"
              :short="collapsed"
              :icon="router.meta.icon"
              :name="router.meta.title || router.meta.name"
              :desc="router.meta.desc"
              :route="router"
              :select="currentRoute?.matched[0]?.path === router.path"
              @on-show="showEvent"
              @on-nav-click="handleNavItemClick"
              @on-hover-start="handleHoverStart"
              @on-hover-end="handleHoverEnd"
            />
          </router-link>
        </div>
      </div>
      <!-- 底部区域 -->
      <div
        class="sidebar-footer pb-[24px] flex flex-row w-full"
        :class="{
          'items-center': collapsed,
          'items-start': !collapsed,
        }"
      >
        <div :style="`transform:translateY(0 px)`">
          <!-- 底部菜单 -->
          <div class="space-y-[33px]">
            <router-link
              v-for="router in footRoute"
              :key="router.path"
              :to="router.path"
              class="block"
            >
              <FootNavItem
                ref="footNavItemRefs"
                :short="collapsed"
                :icon="router.meta.icon"
                :route="router"
                :name="router.meta.title || router.meta.name"
                :select="currentRoute?.matched[0]?.path === router.path"
                @on-show="showEvent"
                @on-nav-click="handleNavItemClick"
                @on-hover-start="handleHoverStart"
                @on-hover-end="handleHoverEnd"
              />
            </router-link>
          </div>
          <!-- 登录区域 -->
          <div
            class="flex items-center cursor-pointer mt-[27px] transition-all duration-300"
            :class="[collapsed ? 'w-[32px] h-[32px] justify-center' : 'w-[204px] pl-[8px] justify-start']"
          >
            <!-- 折叠状态：只显示头像，启用tooltip -->
            <template v-if="collapsed">
              <t-tooltip
                overlay-class-name="aix-tooltip"
                :content="authStageStore.currentUser"
                theme="light"
                placement="top-right"
                destroy-on-close
              >
                <div class="w-[32px] h-[32px]">
                  <Avatar
                    :src="getAvatarIconUrl"
                    :name="authStageStore.currentUser || ''"
                    :size="32"
                  />
                </div>
              </t-tooltip>
            </template>

            <!-- 展开状态：显示头像和用户名，不启用tooltip -->
            <template v-else>
              <div class="w-[32px] h-[32px] flex-shrink-0">
                <Avatar
                  :src="getAvatarIconUrl"
                  :name="authStageStore.currentUser || ''"
                  :size="32"
                />
              </div>
              <div class="text-white ml-3 truncate flex-1 min-w-0">
                {{ authStageStore.currentUser }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </t-aside>

    <!-- SecondLevelSideBar：只在折叠状态且hover时显示，增强条件判断 -->
    <!-- <transition name="slide-right">
      <div
        v-if="!collapsed"
        class="second-level-sidebar-container"
        :style="{ left: collapsed ? '75px' : '350px' }"
        @mouseenter="handleSecondLevelMouseEnter"
        @mouseleave="handleSecondLevelMouseLeave"
      >
        <SecondLevelSideBar
          :is-hover="true"
          :hover-route="hoverRoute"
        />
      </div>
    </transition> -->

    <!-- 隐形的hover桥接区域：连接NavItem和SecondLevelSideBar，只在折叠状态显示 -->
    <!-- <div
      v-if="collapsed && showSecondLevelSideBar"
      class="hover-bridge"
      :style="{
        left: '70px',
        top: hoverBridgeTop + 'px',
        height: '48px',
      }"
      @mouseenter="handleBridgeMouseEnter"
      @mouseleave="handleBridgeMouseLeave"
    /> -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount, onUnmounted, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useWindowSize, watchDebounced, useStorage } from '@vueuse/core';
import SelectGameCode from '@/components/SelectGame/SelectGameCode.vue';
import Logo from '@/components/Layout/Logo.vue';
import NavItem from '@/components/Layout/NavItem.vue';
import FootNavItem from '@/components/Layout/FootNavItem.vue';
import SecondLevelSideBar from '@/components/Layout/SecondLevelSideBar.vue';
import { useRouterStore } from '@/store/global/router.store';
import { storeToRefs } from 'pinia';
import { isString, isUndefined } from 'lodash-es';
import Avatar from '@/components/Avatar.vue';
import { useAuthStageStore } from '@/store/global/auth.store';
import type { RouteRecordNormalized } from 'vue-router';

// 使用store中的collapsed状态
const routerStore = useRouterStore();
const { allRoutes, nowGameAllowRoutes, collapsed, isShowSecondMenu } = storeToRefs(routerStore);

const currentRoute = useRoute();
const authStageStore = useAuthStageStore();
const { currentUser } = storeToRefs(useAuthStageStore());

// 侧边栏DOM引用 - 修改为any类型以兼容TDesign组件
const sidebarRef = ref<any>();
// NavItem组件引用数组
const navItemRefs = ref<any[]>([]);
const footNavItemRefs = ref<any[]>([]);

// SecondLevelSideBar相关状态
const showSecondLevelSideBar = ref(false);
const hoverRoute = ref<RouteRecordNormalized | null>(null);
const hoverTimer = ref<NodeJS.Timeout | null>(null);
const currentHoverNavItem = ref<any>(null);

// 新增：展开过程中的状态标记
const isExpanding = ref(false);

// hover桥接区域的位置
const hoverBridgeTop = ref(0);

const [TOP_LEVEL, FOOT_LEVEL] = [1, -1];
const getRoutesByLevel = (level: number) =>
  allRoutes.value
    .filter(
      (route) =>
        route.meta.level === level && isString(route.name) && !!nowGameAllowRoutes.value.includes(route.name as string),
    )
    .sort((pre, nex) => (Number(pre.meta.index) - Number(nex.meta.index) > 0 ? 1 : -1));

const topRoute = computed(() => getRoutesByLevel(TOP_LEVEL));
const footRoute = computed(() => getRoutesByLevel(FOOT_LEVEL));

const MIN_POINT = 1728 + 1; // 屏幕最小的展示断点
const { width } = useWindowSize();
const isClickCollapsed = useStorage('menu.isClickCollapsed', false, localStorage); // 用户是否点击了折叠-展开按钮

/**
 * 清除hover定时器
 */
const clearHoverTimer = () => {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value);
    hoverTimer.value = null;
  }
};

/**
 * 隐藏SecondLevelSideBar
 */
const hideSecondLevelSideBar = () => {
  showSecondLevelSideBar.value = false;
  hoverRoute.value = null;
  currentHoverNavItem.value = null;

  // 重置所有NavItem的hover状态
  [...navItemRefs.value, ...footNavItemRefs.value].forEach((navItem) => {
    if (navItem && navItem.setHoverState) {
      navItem.setHoverState(false);
    }
  });
};

// 处理NavItem点击事件 - 展开侧边栏
const handleNavItemClick = () => {
  // 设置展开状态标记
  isExpanding.value = true;

  // 立即清除所有定时器和状态
  clearHoverTimer();

  // 立即隐藏SecondLevelSideBar
  hideSecondLevelSideBar();

  // 展开侧边栏
  collapsed.value = false;
  isClickCollapsed.value = true; // 标记为用户手动操作

  // 在动画完成后重置展开状态标记
  setTimeout(() => {
    isExpanding.value = false;
  }, 300); // 与CSS动画时间保持一致
};

// 监听collapsed状态变化，确保展开时隐藏SecondLevelSideBar
watch(
  collapsed,
  (newCollapsed, oldCollapsed) => {
    if (!newCollapsed && oldCollapsed) {
      // 从折叠变为展开时，立即隐藏SecondLevelSideBar
      isExpanding.value = true;
      hideSecondLevelSideBar();

      // 延迟重置展开标记
      setTimeout(() => {
        isExpanding.value = false;
      }, 300);
    } else if (newCollapsed && !oldCollapsed) {
      // 从展开变为折叠时，重置展开标记
      isExpanding.value = false;
    }
  },
  { immediate: false },
);

/**
 * 处理NavItem hover开始事件
 * 功能：只在折叠状态且非展开过程中时显示SecondLevelSideBar并设置对应的路由数据
 */
const handleHoverStart = (data: { route: RouteRecordNormalized; position: string }) => {
  // 只在折叠状态且非展开过程中时处理hover事件
  if (!collapsed.value || isExpanding.value) {
    return;
  }

  // 清除之前的定时器
  clearHoverTimer();

  // 设置hover路由和显示状态
  hoverRoute.value = data.route;
  showSecondLevelSideBar.value = true;

  // 记录当前hover的NavItem
  const allNavItems = [...navItemRefs.value, ...footNavItemRefs.value];
  currentHoverNavItem.value = allNavItems.find(
    (navItem) => navItem && navItem.route && navItem.route.path === data.route.path,
  );

  // 计算hover桥接区域的位置（这里简化处理，实际可能需要更精确的计算）
  hoverBridgeTop.value = 100; // 根据实际NavItem位置调整

  // 设置路由store中的数据，供SecondLevelSideBar使用
  routerStore.setFirstRoute(data.route);
};

/**
 * 处理NavItem hover结束事件
 * 功能：延迟隐藏SecondLevelSideBar
 */
const handleHoverEnd = () => {
  // 只在折叠状态且非展开过程中时处理hover结束事件
  if (!collapsed.value || isExpanding.value) {
    return;
  }

  // 设置延迟隐藏，给用户时间移动到SecondLevelSideBar或桥接区域
  hoverTimer.value = setTimeout(() => {
    hideSecondLevelSideBar();
  }, 150); // 减少延迟时间，但仍给用户足够时间
};

/**
 * 处理SecondLevelSideBar鼠标进入事件
 * 功能：取消隐藏定时器，保持显示状态
 */
const handleSecondLevelMouseEnter = () => {
  clearHoverTimer();

  // 确保当前hover的NavItem保持hover状态
  if (currentHoverNavItem.value && currentHoverNavItem.value.setHoverState) {
    currentHoverNavItem.value.setHoverState(true);
  }
};

/**
 * 处理SecondLevelSideBar鼠标离开事件
 * 功能：立即隐藏SecondLevelSideBar
 */
const handleSecondLevelMouseLeave = () => {
  hideSecondLevelSideBar();
};

/**
 * 处理桥接区域鼠标进入事件
 */
const handleBridgeMouseEnter = () => {
  clearHoverTimer();
};

/**
 * 处理桥接区域鼠标离开事件
 */
const handleBridgeMouseLeave = () => {
  hoverTimer.value = setTimeout(() => {
    hideSecondLevelSideBar();
  }, 100);
};

// 获取实际的DOM元素
const getSidebarElement = (): HTMLElement | null => {
  if (!sidebarRef.value) return null;

  // 如果是TDesign组件，尝试获取其$el属性
  if (sidebarRef.value.$el) {
    return sidebarRef.value.$el as HTMLElement;
  }

  // 如果直接是DOM元素
  if (sidebarRef.value.nodeType === Node.ELEMENT_NODE) {
    return sidebarRef.value as HTMLElement;
  }

  return null;
};

// 全局点击监听 - 点击侧边栏外部收缩
const handleGlobalClick = (event: MouseEvent) => {
  const sidebarElement = getSidebarElement();
  const target = event.target as Node;

  // 检查是否点击在SecondLevelSideBar上
  const secondLevelElement = document.querySelector('.second-level-sidebar-container');
  const isClickOnSecondLevel = secondLevelElement && secondLevelElement.contains(target);

  // 安全检查：确保sidebarElement存在且有contains方法，并且target存在
  if (
    sidebarElement &&
    typeof sidebarElement.contains === 'function' &&
    target &&
    !sidebarElement.contains(target) &&
    !isClickOnSecondLevel
  ) {
    // 点击在侧边栏和SecondLevelSideBar外部，收缩侧边栏
    collapsed.value = true;
    isClickCollapsed.value = true; // 标记为用户手动操作

    // 同时隐藏SecondLevelSideBar
    hideSecondLevelSideBar();
  }
};

onBeforeMount(() => {
  // 初始化时设置为收缩状态
  if (isUndefined(collapsed.value)) {
    collapsed.value = true;
  }
});

onMounted(() => {
  console.log('collapsed.value: ', collapsed.value);

  // 初始化状态
  if (isUndefined(collapsed.value) && isUndefined(isShowSecondMenu.value)) {
    collapsed.value = width.value <= MIN_POINT;
    isShowSecondMenu.value = width.value > MIN_POINT;
  }

  // 使用nextTick确保DOM已经渲染完成后再添加事件监听
  nextTick(() => {
    // 添加全局点击监听
    document.addEventListener('click', handleGlobalClick);
  });
});

onUnmounted(() => {
  // 清理全局点击监听
  document.removeEventListener('click', handleGlobalClick);

  // 清理定时器
  clearHoverTimer();
});

// 监听窗口大小变化
watchDebounced(width, () => {
  if (!isClickCollapsed.value) {
    collapsed.value = width.value <= MIN_POINT;
    isShowSecondMenu.value = width.value > MIN_POINT;
  }
});

const getAvatarIconUrl = computed(() => {
  return currentUser.value
    ? `https://rhrc.woa.com/photo/150/${currentUser.value}.png`
    : 'data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==';
});

const emits = defineEmits(['onShow']);
const showEvent = (val: boolean) => {
  console.log('父组件showEvent', val);
  emits('onShow', val);
};

defineExpose({ collapsed });
</script>

<style lang="scss" scoped>
.sidebar-container {
  position: relative;
  height: 100%;
}

.close-animation {
  transition-duration: 0.3s;
  transition-property: width;
}

/* SecondLevelSideBar容器样式 */
.second-level-sidebar-container {
  position: absolute;
  top: 0;
  height: 100%;
  z-index: 1000;
  transition: left 0.3s ease-in-out;
}

/* hover桥接区域样式 */
.hover-bridge {
  position: absolute;
  width: 20px; /* 桥接区域宽度 */
  background: transparent;
  z-index: 999;
  pointer-events: auto;
}

/* SecondLevelSideBar滑入动画 */
.slide-right-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-right-enter-to,
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}

:global(.aix-tooltip .t-popup__content--arrow .t-popup__arrow) {
  bottom: auto;
}

.aside-custom {
  /* 脱离文档流，固定在左侧 */
  position: fixed;
  top: 0;
  left: 0;
  /* 确保层级最高 */
  z-index: 9999;
  /* 原有的样式保留 */
  height: 100vh;
  background: #020202;
  will-change: width;
  /* 宽度过渡动画 */
  transition: width 0.3s ease-in-out;
  /* 避免内容溢出时出现滚动条（可选） */
  overflow: hidden;
}

.main-content {
  /* 若侧边栏固定宽度，可给主内容加左侧 padding 避免被遮挡 */
  padding-left: 236px; /* 侧边栏默认宽度，折叠后可通过 JS 动态调整 */
  /* 主内容自身的过渡（可选，若需要随侧边栏宽度变化有动画） */
  transition: padding-left 0.3s ease-in-out;
}
</style>
